import * as Sentry from '@sentry/nextjs';

import {
  SENTRY_DATA_COLLECTION,
  SENTRY_DSN,
  SENTRY_ENABLED,
  SENTRY_ENVIRONMENT,
  SENTRY_RELEASE,
} from './sentryConfig';

// Quien estaba usando el dashboard cuando fallo: agente, rol y clinica. Nada del paciente ni el token.
function dashboardContext(): {
  agentId?: string;
  roles?: string;
  clinic?: string;
} {
  try {
    const raw = window.sessionStorage.getItem('dashboard-storage');
    if (!raw) return {};
    const state = JSON.parse(raw)?.state;
    return {
      agentId: state?.userLoginResponse?.agentId,
      roles: state?.userLoginResponse?.roles,
      clinic: state?.clinic?.city,
    };
  } catch {
    return {};
  }
}

// El navegador de Instagram inyecta un script que habla con la app (dispatchToBridge) y falla fuera de ella
// con mensajes genericos como "InvalidAccessError" (27-oct, HOLAGLOW-FRONT-V/W): se reconoce por la traza.
const IN_APP_BRIDGE_FUNCTIONS = ['dispatchToBridge', 'logLoginFormFocused'];

function isInAppBrowserBridgeError(event: Sentry.ErrorEvent): boolean {
  return (event.exception?.values ?? []).some(value =>
    (value.stacktrace?.frames ?? []).some(frame =>
      IN_APP_BRIDGE_FUNCTIONS.includes(frame.function ?? '')
    )
  );
}

export function initSentryClient() {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,
    enabled: SENTRY_ENABLED,
    dataCollection: SENTRY_DATA_COLLECTION,
    tracesSampleRate: 0.05,
    // Grabacion solo de las sesiones con error, con todo el texto y los campos tapados.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        // Solo se graba el dashboard: el plan gratuito trae ~50 grabaciones al mes y la web se las comia
        // en dias (24-oct: 8 en la primera hora por desajustes de hidratacion en /tratamientos).
        beforeErrorSampling: () => window.location.pathname.startsWith('/dashboard'),
      }),
      // Las llamadas a nuestras APIs que responden 5xx salen como error aunque el front lo capture. Solo las
      // nuestras: sin filtro entraban tambien las de terceros (25-oct, un 504 de Contentsquare).
      Sentry.httpClientIntegration({
        failedRequestStatusCodes: [[500, 599]],
        failedRequestTargets: [/holaglow[a-z]*api[a-z]*\.azurewebsites\.net/],
      }),
    ],
    // Desajustes de hidratacion de React (#418, #423, #425): React los corrige solo en el navegador y el
    // usuario no ve nada roto; saltaban en cada carga de algunas fichas de la web.
    ignoreErrors: [
      'ResizeObserver loop',
      'Hydration failed',
      'There was an error while hydrating',
      'Minified React error #418',
      'Minified React error #423',
      'Minified React error #425',
      // Codigo que inyectan los navegadores internos de Instagram, Facebook, etc. (no es nuestro): lee los
      // datos estructurados esperando un objeto, o llama a su puente con la app y falla fuera de ella.
      '@context',
      'webkit.messageHandlers',
      'Java bridge method invocation error',
      '_META.IABInnerFrame',
    ],
    beforeSend(event) {
      if (isInAppBrowserBridgeError(event)) return null;
      const area = window.location.pathname.startsWith('/dashboard')
        ? 'dashboard'
        : 'web';
      event.tags = { ...event.tags, area };
      if (area === 'dashboard') {
        const { agentId, roles, clinic } = dashboardContext();
        if (agentId) event.user = { id: agentId };
        if (roles) event.tags.role = roles;
        if (clinic) event.tags.clinic = clinic;
      }
      return event;
    },
  });
}
